import { State, Action, StateContext, Selector } from '@ngxs/store';
import { WidgetProductSetting, WidgetStateModel } from './widget.state.model';
import {
  ResetWidget,
  LogWidgetInteraction,
  LogWidgetInteractionFailure,
  LogWidgetInteractionSuccess,
  AuthWidget,
  AuthWidgetSuccess,
  AuthWidgetFailure,
  SetupWidget,
  SetInputEnabled,
  SetWidgetApiConfig,
  GetWidgetProductSettings,
  SetWidgetProductSettings,
} from './widget.actions';
import { WidgetAnalyticsService } from 'src/app/widget-auth/widget-analytics.service';
import { WidgetAuthService } from 'src/app/widget-auth/widget-auth.service';
import { finalize } from 'rxjs/operators';
import { UserAnalyticModel } from '../models/user-analytic.model';
import { DeveloperAnalyticModel } from '../models/developer-analytic.model';
import {
  AnalyticActivityModel,
  AnalyticUserActivityModel,
} from '../models/analytic-activity.model';
import { WidgetUserTypeEnum } from '../models/widget-user-type.enum';
import { WidgetPlatformEnum } from '../models/widget-platform.enum';
import { WidgetInteractionEnum } from '../models/widget-interaction.enum';
import { AnalyticResponseModel } from '../models/analytic-response.model';
import { Observable } from 'rxjs';
import { plainToClass } from 'class-transformer';
import { Injectable } from '@angular/core';
import { WidgetConfigService } from '../widget-config';
import { HumanInterface } from 'src/app/engine/models/human.interface';

declare const Human: HumanInterface;

const initialState: WidgetStateModel = {
  // auth state
  userType: WidgetUserTypeEnum.User,
  authenticating: false,
  authenticated: false,
  // widget config
  widgetUrlConfig: {
    id: '',
    type: '',
  },
  widgetApiConfig: null,
  // auth data
  parentUrl: {
    full: '',
    domain: '',
  },
  embedded: false,
  // engine params
  engineVersion: '',
  engineAPIVersion: '',
  inputEnabled: true,
  // widget settings
  widgetSettings: undefined,
};

@State<WidgetStateModel>({
  name: 'widget',
  defaults: initialState,
})
@Injectable()
export class WidgetState {
  @Selector()
  static getWidgetState(state: WidgetStateModel) {
    return state;
  }

  @Selector()
  static getUserType(state: WidgetStateModel) {
    return state.userType;
  }

  @Selector()
  static getWidgetAuthenticating(state: WidgetStateModel) {
    return state.authenticating;
  }

  @Selector()
  static getWidgetAuthenticated(state: WidgetStateModel) {
    return state.authenticated;
  }

  @Selector()
  static getInputEnabled(state: WidgetStateModel) {
    return state.inputEnabled;
  }

  @Selector()
  static getEmbedded(state: WidgetStateModel) {
    return state.embedded;
  }

  @Selector()
  static getParentURL(state: WidgetStateModel) {
    return state.parentUrl.full;
  }

  @Selector()
  static getWidgetUrlConfig(state: WidgetStateModel) {
    return state.widgetUrlConfig;
  }

  @Selector()
  static getWidgetApiUIConfig(state: WidgetStateModel) {
    return state.widgetApiConfig;
  }

  @Selector()
  static getWidgetSettings(state: WidgetStateModel): WidgetProductSetting[] {
    return state.widgetSettings;
  }

  @Action(SetupWidget)
  setupWidget(
    context: StateContext<WidgetStateModel>,
    { hostingURL, embedded, widgetConfig }: SetupWidget,
  ) {
    // extract host + port (optional) from url, match: 'protocol://host.name:port/path' -> 'host.name:port'
    const match = hostingURL.match(/:\/\/(.[^/]+)/);
    const domain = match && match[1] ? match[1] : hostingURL;

    context.patchState({
      // user type
      userType: widgetConfig.uaid ? WidgetUserTypeEnum.User : WidgetUserTypeEnum.Developer,
      // embedded state
      embedded: embedded,
      // config
      widgetUrlConfig: widgetConfig,
      // hosting
      parentUrl: {
        full: hostingURL,
        domain: domain,
      },
    });
  }

  @Action(GetWidgetProductSettings)
  getWidgetProductSettings(
    context: StateContext<WidgetStateModel>,
    { productAccountId }: GetWidgetProductSettings,
  ) {
    // read widget settings
    this.widgetConfigService.getWidgetSettings(productAccountId).subscribe(({ settings }) => {
      context.dispatch(new SetWidgetProductSettings(settings));
    });
  }

  @Action(SetWidgetProductSettings)
  setWidgetProductSettings(
    context: StateContext<WidgetStateModel>,
    { widgetSettings }: SetWidgetProductSettings,
  ) {
    context.patchState({
      widgetSettings,
    });
  }

  @Action(AuthWidget)
  authWidget(context: StateContext<WidgetStateModel>, action: AuthWidget) {
    const widgetState = context.getState();
    const { userType, widgetUrlConfig: widgetConfig } = widgetState;

    const authRequest$: Observable<AnalyticResponseModel> =
      userType === WidgetUserTypeEnum.User
        ? // user auth: via uaid
          this.authService.authUser(widgetConfig.uaid)
        : // developer auth: via developer key
          this.authService.authDeveloper(
            this.analyticsService.buildDeveloperAnalytic(
              widgetState,
              WidgetInteractionEnum.Init,
              WidgetPlatformEnum.WidgetAPI,
              this.buildAppActivityData(widgetState),
            ), // build developer auth
          );

    // begin auth
    context.patchState({ authenticating: true });

    // check auth
    authRequest$
      .pipe(finalize(() => context.patchState({ authenticating: false })))
      .subscribe((response) => {
        const success = response.success;
        context.patchState({ authenticated: success });

        context.dispatch(
          success
            ? new AuthWidgetSuccess(userType, response)
            : new AuthWidgetFailure(userType, response),
        );
      });
  }

  @Action(LogWidgetInteraction)
  logWidgetInteraction(
    context: StateContext<WidgetStateModel>,
    { interaction, source }: LogWidgetInteraction,
  ) {
    const widgetState = context.getState();
    const userType = widgetState.userType;

    const analytic: UserAnalyticModel | DeveloperAnalyticModel =
      userType === WidgetUserTypeEnum.User
        ? this.buildUserAnalytic(widgetState, interaction, source)
        : this.analyticsService.buildDeveloperAnalytic(
            widgetState,
            interaction,
            WidgetPlatformEnum.Widget,
            this.buildAppActivityData(widgetState),
          );

    // log interaction
    this.analyticsService.sendAnalytic(analytic).subscribe((response) => {
      if (response.success) {
        context.dispatch(new LogWidgetInteractionSuccess(interaction, response));
      } else {
        context.dispatch(new LogWidgetInteractionFailure(interaction, response));
      }
    });
  }

  @Action(ResetWidget)
  resetWidget(context: StateContext<WidgetStateModel>, action: ResetWidget) {
    context.setState(initialState);
  }

  private buildAppActivityData(widgetState: WidgetStateModel): AnalyticActivityModel {
    const { engineVersion, engineAPIVersion, widgetUrlConfig: widgetConfig } = widgetState;

    // require activity data
    const baseActivityData: AnalyticActivityModel = {
      engine_version: engineVersion,
      api_version: engineAPIVersion,
    };

    // include widget params
    Object.entries(widgetConfig)
      // exclude developer key
      .filter(([key]) => key !== 'dk')
      // set url param: i.e., ?ui-info=true -> { ... 'url_param_ui-info': 'true' }
      .forEach(([key, value]) => {
        baseActivityData[`url_param_${key}`] = value;
      });

    return plainToClass(AnalyticActivityModel, baseActivityData);
  }

  @Action(SetInputEnabled)
  setInputEnabled(context: StateContext<WidgetStateModel>, action: SetInputEnabled) {
    context.patchState({
      inputEnabled: action.enabled,
    });
  }

  @Action(SetWidgetApiConfig)
  setWidgetApiConfig(context: StateContext<WidgetStateModel>, action: SetWidgetApiConfig) {
    context.patchState({
      widgetApiConfig: action.config,
    });
  }

  private buildUserAnalytic(
    widgetState: WidgetStateModel,
    activity: string,
    source: string,
  ): UserAnalyticModel {
    const { widgetUrlConfig: widgetConfig, parentUrl } = widgetState;
    const uaid = widgetConfig.uaid;
    const domain = parentUrl.domain;

    const activityData: AnalyticUserActivityModel = {
      ...this.buildAppActivityData(widgetState),
      // include user keys + host
      parent_analytic_identifier: uaid,
      referer_domain: domain,
      // activity source
      [`${activity}_source`]: source,
    };

    const analytic: UserAnalyticModel = {
      analytic_platform: WidgetPlatformEnum.Widget,
      analytic_type: activity,
      analytic_data: activityData,
    };

    return plainToClass(UserAnalyticModel, analytic);
  }

  ngxsOnInit(context: StateContext<WidgetStateModel>) {
    // set fixed state
    context.patchState({
      engineVersion: Human.VERSION,
      engineAPIVersion: Human.API_VERSION,
    });
  }

  constructor(
    private authService: WidgetAuthService,
    private analyticsService: WidgetAnalyticsService,
    private widgetConfigService: WidgetConfigService,
  ) {}
}
